import { mutate } from 'swr';

import { publicConfig } from '@public-config';

import { DiagramConnection } from 'models';
import { WireSizeChargingHelpers, WireSizingEnabledConnections } from 'helpers/WireSizeChargingHelpers';

import { ApiService } from 'services/ApiService';
import { state as currentProjectState } from 'state/current-project';

type WireSizeChargingResponse = {
    charged: number;
    added: string[];
    wireSizingEnabledConnections: WireSizingEnabledConnections;
};

const WireSizeChargingService = {
    get: async (projectId: string): Promise<WireSizingEnabledConnections> => {
        const { wireSizingEnabledConnections } = await ApiService.get<WireSizeChargingResponse>(
            `${publicConfig.urls.api}/projects/${projectId}/wire-sizing-enabled-connections`,
            { throwError: true },
        );

        return wireSizingEnabledConnections;
    },

    refresh: (projectId: string) => {
        return mutate(WireSizeChargingHelpers.swr.enabledConnections(projectId));
    },

    cache: (projectId: string, connections: WireSizingEnabledConnections) => {
        return mutate(WireSizeChargingHelpers.swr.enabledConnections(projectId), connections, { revalidate: false });
    },

    enable: async (connections: DiagramConnection | DiagramConnection[]) => {
        const projectId = currentProjectState.project?.id;
        const pending = Array.isArray(connections) ? connections : [connections];

        if (!projectId || !pending.length) {
            return;
        }

        const { wireSizingEnabledConnections } = await ApiService.post<WireSizeChargingResponse>(
            `${publicConfig.urls.api}/projects/${projectId}/charge-wire-sizing`,
            { connections: pending },
            { throwError: true },
        );

        await WireSizeChargingService.cache(projectId, wireSizingEnabledConnections);
    },

    split: async (projectId: string, connectionId: string, connections: DiagramConnection[]) => {
        if (!connections.length) {
            return;
        }

        const { wireSizingEnabledConnections } = await ApiService.post<WireSizeChargingResponse>(
            `${publicConfig.urls.api}/projects/${projectId}/split-wire-sizing`,
            { connectionId, connections },
            { throwError: true },
        );

        await WireSizeChargingService.cache(projectId, wireSizingEnabledConnections);
    },
};

export { WireSizeChargingService };
